import { useCallback, useEffect, useState } from "react";
import styles from "./HomePage.module.css";

import PageWrapper from "../components/layout/PageWrapper";
import razLight from "../assets/images/raz/raz-light.jpg";

import HeroCard from "../components/homepage/HeroCard";
import HubCard from "../components/homepage/HubCard";
import InfoBar from "../components/navigation/InfoBar";
import FeaturedMatchCard from "../components/homepage/FeaturedMatchCard";
import WeekendMatchesRail from "../components/homepage/WeekendMatchesRail";
import CurrentInternationalRail from "../components/homepage/CurrentInternationalRail";
import AutoContentRail from "../components/ui/AutoContentRail";

import { tournaments2026 } from "../data/tournamentMeta";

/* ICONS */
import MusicIcon from "../components/icons/MusicIcon";
import AwardIcon from "../components/icons/AwardIcon";
import GridIcon from "../components/icons/GridIcon";
import LiveScoresIcon from "../components/icons/LiveScoresIcon";
import FixturesIcon from "../components/icons/FixturesIcon";
import ResultsIcon from "../components/icons/ResultsIcon";
import StatsIcon from "../components/icons/StatsIcon";
import VideoIcon from "../components/icons/VideoIcon";
import UsersIcon from "../components/icons/UsersIcon";
import AirplaneIcon from "../components/icons/AirplaneIcon";
import TicketIcon from "../components/icons/TicketIcon";
import TruckIcon from "../components/icons/TruckIcon";
import FileTextIcon from "../components/icons/FileTextIcon";
import BookOpenIcon from "../components/icons/BookOpenIcon";
import StarIcon from "../components/icons/StarIcon";
import CalendarIcon from "../components/icons/CalendarIcon";
import ShoppingBagIcon from "../components/icons/ShoppingBagIcon";
import ArchiveIcon from "../components/icons/ArchiveIcon";

/* WOMEN'S RUGBY BANNER */
import wxvWomensBanner from "../assets/images/raz/wxv-womens-banner.png";

/* IMAGES */
import anthemsImage from "../assets/images/raz/anthems-page.png";
import tournamentsImage from "../assets/images/raz/tournament-hub-page.png";
import matchCenterImage from "../assets/images/raz/Match-center.png";
import matchDayImage from "../assets/images/raz/match-day-journey.png";
import rugbyStudioImage from "../assets/images/raz/Mediahub.jpg";
import fanzoneImage from "../assets/images/raz/fanzone-hub.png";
import newsImage from "../assets/images/raz/news-hero.png";
import insideTheGameImage from "../assets/images/raz/inside-the-game.png";
import calendarImage from "../assets/images/raz/calendar-hero2.jpg";
import merchImage from "../assets/images/raz/Merch.png";
import heritageImage from "../assets/images/raz/heritage-hub.png";
import definingMomentsImage from "../assets/images/raz/moments-hero.jpg";

/* DATA */
import { getMatches } from "../data/matchesAdapter";
import type { MatchData } from "../data/matches/types";

/**
 * HOME PAGE — WAVE 2
 * --------------------------------------------------
 * Single paid RAZ home.
 * Super content is now folded into the main paid home.
 * We are not deleting the old SuperHomePage yet; this page
 * becomes the live source of truth.
 */

export default function HomePage() {
  const [matches, setMatches] = useState<MatchData[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(true);

  /*
   * IDs of the matches currently displayed in the
   * Featured Match block.
   */
  const [featuredMatchIds, setFeaturedMatchIds] = useState<number[]>([]);

  const featuredTournament =
    tournaments2026.find((t) => t.conceptId === "nations-championship") ??
    tournaments2026.find((t) => t.status === "active") ??
    tournaments2026[0];

  useEffect(() => {
    async function load() {
      try {
        const data = await getMatches();
        setMatches(data);
      } catch (error) {
        console.error("Failed to load matches:", error);
      } finally {
        setLoadingMatches(false);
      }
    }

    load();
  }, []);

  /*
   * Keep the callback reference stable so the Featured
   * component does not repeatedly trigger this effect.
   */
  const handleFeaturedMatchesChange = useCallback(
    (matchIds: number[]) => {
      setFeaturedMatchIds(matchIds);
    },
    []
  );

  /*
   * Remove matches already displayed in the Featured
   * section before passing the remaining data to the
   * Weekend Matches section.
   */
  const weekendMatches = matches.filter(
    (match) => !featuredMatchIds.includes(match.id)
  );

  return (
    <PageWrapper imageUrl={razLight}>
      <main className={styles.page}>
        <HeroCard />

        <InfoBar />

        {/* ====== WXV WOMEN'S RUGBY BANNER ====== */}
        <section className={styles.womensBannerSection}>
          <img
            src={wxvWomensBanner}
            alt="WXV Women's Rugby"
            className={styles.womensBannerImage}
          />
        </section>
        {/* ====== END WXV WOMEN'S RUGBY BANNER ====== */}

        <FeaturedMatchCard
          matches={matches}
          loading={loadingMatches}
          onFeaturedMatchesChange={handleFeaturedMatchesChange}
        />

        <WeekendMatchesRail
          matches={weekendMatches}
          loading={loadingMatches}
        />

        <CurrentInternationalRail
          matches={matches}
          loading={loadingMatches}
        />

        <section className={styles.railSection}>
          <AutoContentRail autoAdvance>
            {featuredTournament && (
              <HubCard
                title={featuredTournament.name}
                image={tournamentsImage}
                to={featuredTournament.route}
                features={[
                  {
                    label:
                      featuredTournament.heroBadge || "FEATURED TOURNAMENT",
                    icon: <AwardIcon />,
                  },
                  {
                    label:
                      featuredTournament.heroSubtitle ||
                      "Current global focus",
                    icon: <StarIcon />,
                  },
                ]}
              />
            )}

            <HubCard
              title="Notifications"
              image={newsImage}
              to="/notifications"
              features={[
                {
                  label: "Match alerts & key rugby moments",
                  icon: <CalendarIcon />,
                },
                {
                  label: "Tournament updates you opt into",
                  icon: <StarIcon />,
                },
              ]}
            />

            <HubCard
              title="Anthems"
              image={anthemsImage}
              to="/anthems"
              features={[{ label: "National Anthems", icon: <MusicIcon /> }]}
            />

            <HubCard
              title="Tournaments"
              image={tournamentsImage}
              to="/tournaments"
              features={[
                { label: "Men’s Tournaments", icon: <AwardIcon /> },
                { label: "Women’s Tournaments", icon: <GridIcon /> },
              ]}
            />

            <HubCard
              title="Match Center"
              image={matchCenterImage}
              to="/match-center"
              features={[
                { label: "Live Scores", icon: <LiveScoresIcon /> },
                { label: "Fixtures", icon: <FixturesIcon /> },
                { label: "Results", icon: <ResultsIcon /> },
                { label: "Stats", icon: <StatsIcon /> },
              ]}
            />

            <HubCard
              title="Matchday Journeys"
              image={matchDayImage}
              to="/matchday-journeys"
              features={[
                { label: "Flights", icon: <AirplaneIcon /> },
                { label: "Tickets", icon: <TicketIcon /> },
                { label: "Transport", icon: <TruckIcon /> },
              ]}
            />

            <HubCard
              title="The Rugby Studio"
              image={rugbyStudioImage}
              to="/media"
              features={[
                { label: "Match Videos", icon: <VideoIcon /> },
                { label: "Podcasts & Audio", icon: <VideoIcon /> },
              ]}
            />

            <HubCard
              title="Fanzone"
              image={fanzoneImage}
              to="/fanzone"
              features={[
                { label: "Community & Loyalty", icon: <UsersIcon /> },
              ]}
            />

            <HubCard
              title="News"
              image={newsImage}
              to="/news"
              features={[
                { label: "Headlines & Analysis", icon: <FileTextIcon /> },
              ]}
            />

            <HubCard
              title="Inside the Game"
              image={insideTheGameImage}
              to="/inside-the-game"
              features={[
                { label: "Laws & Referees", icon: <BookOpenIcon /> },
                { label: "Fantasy & Analysis", icon: <StarIcon /> },
              ]}
            />

            <HubCard
              title="Calendar"
              image={calendarImage}
              to="/calendar"
              features={[
                {
                  label: "Global fixtures, key dates & iconic stadiums",
                  icon: <CalendarIcon />,
                },
              ]}
            />

            <HubCard
              title="Merch"
              image={merchImage}
              to="/merch"
              features={[
                { label: "Official Team Gear", icon: <ShoppingBagIcon /> },
              ]}
            />

            <HubCard
              title="Heritage"
              image={heritageImage}
              to="/heritage"
              features={[
                {
                  label: "Legends, Squads & Champions",
                  icon: <ArchiveIcon />,
                },
              ]}
            />

            <HubCard
              title="Defining Rugby Moments"
              image={definingMomentsImage}
              to="/defining-moments"
              features={[
                {
                  label: "The moments that shaped the game",
                  icon: <ArchiveIcon />,
                },
              ]}
            />
          </AutoContentRail>
        </section>
      </main>
    </PageWrapper>
  );
}